// Reusable IOSentra data table — glass card, sticky header, hover rows, skeleton
// loading + empty state. Column cells render via a `render` fn so each page
// stays declarative.
//
// Opt-in features (all backward-compatible — existing callers are unaffected):
//   • Sorting    — set `sortable` on a column (+ optional `sortValue` accessor).
//   • Pagination — pass `pageSize` to page client-side with a footer.
//   • Selection  — pass `selectable` + `selectedIds`/`onSelectionChange` for a
//     checkbox column; render `bulkActions` to act on the selection.
//   • v2 toolbar — pass `tableId` to get a "Columns" menu (hide/show columns,
//     persisted) + a density toggle (comfortable/compact, persisted). Mark a
//     column `hideable` and optionally `defaultHidden`. A `toolbar` slot lets a
//     page add its own controls inline with the table actions.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
  SlidersHorizontal, Columns3, Check,
} from 'lucide-react';
import { Skeleton, EmptyState } from './primitives';
import type { LucideIcon } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Make this column's header click-to-sort. */
  sortable?: boolean;
  /** Value used for sorting (defaults to row[key]). Return string|number|Date. */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  /** Allow the user to hide this column from the Columns menu (needs tableId). */
  hideable?: boolean;
  /** Hidden by default until the user enables it (implies hideable). */
  defaultHidden?: boolean;
  /**
   * Absorb the table's leftover width. With table-layout:auto, short columns
   * size to content and slack pools into the last column; mark the primary
   * column `grow` so the table fills its container and the extra space lands
   * on a meaningful column instead of a trailing gap.
   */
  grow?: boolean;
}

type SortDir = 'asc' | 'desc';
type Density = 'comfortable' | 'compact';

function usePersisted<T>(key: string | undefined, initial: T): [T, (v: T) => void] {
  const [val, setVal] = useState<T>(() => {
    if (!key || typeof localStorage === 'undefined') return initial;
    try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as T) : initial; } catch { return initial; }
  });
  const set = (v: T) => {
    setVal(v);
    if (key && typeof localStorage !== 'undefined') { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore quota */ } }
  };
  return [val, set];
}

export function DataTable<T extends { id: string }>({
  columns, rows, loading, emptyIcon, emptyTitle, emptyHint, onRowClick,
  pageSize, selectable, selectedIds, onSelectionChange, bulkActions,
  tableId, toolbar,
  serverMode, page: serverPage, total: serverTotal, onPageChange, onSortChange,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  emptyHint?: string;
  onRowClick?: (row: T) => void;
  /** Rows per page. Omit to show all rows (current behavior). */
  pageSize?: number;
  /** Server-side pagination (audit PERF): `rows` is already the current page; the
   *  parent owns page/total. Default OFF → existing client-slice behavior. */
  serverMode?: boolean;
  /** Controlled current page (0-based) — server mode only. */
  page?: number;
  /** Total row count from the server — server mode only. */
  total?: number;
  /** Page-change callback — server mode only (parent refetches). */
  onPageChange?: (page: number) => void;
  /** Sort-change callback — server mode only (parent sorts via the query). When
   *  provided, header clicks call this instead of sorting the current page. */
  onSortChange?: (key: string, dir: SortDir) => void;
  /** Show a checkbox column + select-all. Requires selectedIds/onSelectionChange. */
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  /** Toolbar shown above the table when ≥1 row is selected (bulk actions). */
  bulkActions?: (selected: Set<string>) => ReactNode;
  /** Stable id → enables the v2 toolbar (column visibility + density), persisted
   *  per table under this id. Omit to keep the plain table. */
  tableId?: string;
  /** Extra controls rendered in the v2 toolbar row (left of Columns/Density). */
  toolbar?: ReactNode;
}) {
  const { t } = useTranslation('components');
  const align = (a?: string) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [page, setPage] = useState(0);

  // ── v2: density + column visibility (persisted per tableId) ────────────────
  const hasToolbar = !!tableId || !!toolbar;
  const [density, setDensity] = usePersisted<Density>(tableId ? `tk.tbl.${tableId}.density` : undefined, 'comfortable');
  const defaultHidden = useMemo(
    () => columns.filter((c) => c.defaultHidden).map((c) => c.key),
    [columns],
  );
  const [hidden, setHidden] = usePersisted<string[]>(tableId ? `tk.tbl.${tableId}.hidden` : undefined, defaultHidden);
  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  // Hide columns the user/preset hid. Without a tableId there's no Columns menu,
  // but a `defaultHidden` column is still honored (it just can't be re-shown).
  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenSet.has(c.key)),
    [columns, hiddenSet],
  );
  const hideableColumns = columns.filter((c) => c.hideable || c.defaultHidden);
  const cellPad = density === 'compact' ? 'px-3 py-1.5' : 'px-4 py-3';

  const sorted = useMemo(() => {
    // In server mode the server already sorted; don't reorder the page locally.
    if (serverMode || !sort || !rows) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = (r: T): string | number => {
      const raw = col.sortValue ? col.sortValue(r) : (r as Record<string, unknown>)[col.key];
      if (raw == null) return sort.dir === 'asc' ? Infinity : -Infinity;
      if (raw instanceof Date) return raw.getTime();
      if (typeof raw === 'number') return raw;
      return String(raw).toLowerCase();
    };
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  // Server mode: `rows` IS the current page, `total`/`page` are parent-owned.
  // Client mode (default): slice the sorted set locally as before.
  const total = serverMode ? (serverTotal ?? 0) : (sorted?.length ?? 0);
  const curPage = serverMode ? (serverPage ?? 0) : page;
  const pageCount = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const safePage = Math.min(curPage, pageCount - 1);
  const pageRows = serverMode
    ? sorted // already the page from the server
    : (pageSize && sorted ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted);
  const goToPage = (p: number) => { if (serverMode) onPageChange?.(p); else setPage(p); };

  const toggleSort = (key: string) => {
    if (serverMode) {
      // Server sorts via the query; report the new key/dir and let the parent refetch.
      if (onSortChange) {
        const dir: SortDir = sort?.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
        setSort({ key, dir });
        onSortChange(key, dir);
      }
      return;
    }
    setPage(0);
    setSort((s) => s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' });
  };

  // Selection helpers (operate over the FULL filtered set, not just this page).
  const allIds = sorted?.map((r) => r.id) ?? [];
  const allSelected = selectable && allIds.length > 0 && allIds.every((id) => selectedIds?.has(id));
  const someSelected = selectable && allIds.some((id) => selectedIds?.has(id)) && !allSelected;
  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? new Set() : new Set(allIds));
  };
  const toggleOne = (id: string) => {
    if (!onSelectionChange || !selectedIds) return;
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange(next);
  };

  const colSpan = visibleColumns.length + (selectable ? 1 : 0);

  return (
    <div className="space-y-2">
      {hasToolbar && (
        <div className="flex flex-wrap items-center gap-2">
          {toolbar}
          <div className="ms-auto flex items-center gap-2">
            {tableId && hideableColumns.length > 0 && (
              <ColumnsMenu
                columns={hideableColumns}
                hiddenSet={hiddenSet}
                onToggle={(key) => {
                  const next = new Set(hiddenSet);
                  next.has(key) ? next.delete(key) : next.add(key);
                  setHidden([...next]);
                }}
                label={t('dataTable.columns')}
              />
            )}
            {tableId && (
              <button
                className="tk-btn-ghost px-2 py-1.5 text-caption"
                onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
                title={t('dataTable.density')}
                aria-label={t('dataTable.density')}
              >
                <SlidersHorizontal size={14} />
                {density === 'compact' ? t('dataTable.densityCompact') : t('dataTable.densityComfortable')}
              </button>
            )}
          </div>
        </div>
      )}
      {selectable && (selectedIds?.size ?? 0) > 0 && bulkActions && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm ring-1 ring-primary/30">
          <span className="text-muted">{t('dataTable.selected', { count: selectedIds!.size })}</span>
          {bulkActions(selectedIds!)}
          <button className="tk-btn-ghost ms-auto px-2 py-1 text-xs" onClick={() => onSelectionChange?.(new Set())}>{t('dataTable.clear')}</button>
        </div>
      )}
      <div className="tk-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 text-xs text-muted backdrop-blur">
            <tr className="border-b border-white/10">
              {selectable && (
                <th className={`w-10 ${cellPad}`}>
                  <input type="checkbox" checked={!!allSelected} ref={(el) => { if (el) el.indeterminate = !!someSelected; }} onChange={toggleAll} aria-label={t('dataTable.selectAll')} />
                </th>
              )}
              {visibleColumns.map((c) => (
                <th key={c.key} className={`${cellPad} font-medium ${align(c.align)} ${c.grow ? 'w-full' : ''}`}>
                  {c.sortable ? (
                    <button className="inline-flex items-center gap-1 hover:text-fg" onClick={() => toggleSort(c.key)}>
                      {c.header}
                      {sort?.key === c.key
                        ? (sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
                        : <ChevronsUpDown size={13} className="opacity-40" />}
                    </button>
                  ) : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className={cellPad} colSpan={colSpan}><Skeleton className="h-5" /></td>
              </tr>
            ))}
            {!loading && total === 0 && (
              <tr><td colSpan={colSpan}><EmptyState icon={emptyIcon} title={emptyTitle} hint={emptyHint} /></td></tr>
            )}
            {!loading && pageRows?.map((row) => {
              const selected = selectable && selectedIds?.has(row.id);
              return (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-white/5 transition-colors hover:bg-white/5 ${onRowClick ? 'cursor-pointer' : ''} ${selected ? 'bg-primary/5' : ''}`}
                >
                  {selectable && (
                    <td className={cellPad} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={!!selected} onChange={() => toggleOne(row.id)} aria-label={t('dataTable.selectRow')} />
                    </td>
                  )}
                  {visibleColumns.map((c) => (
                    <td key={c.key} className={`${cellPad} ${align(c.align)} ${c.grow ? 'w-full' : ''} ${c.className ?? ''}`}>
                      {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageSize && total > pageSize && (
        <div className="flex items-center justify-between px-1 text-xs text-muted">
          <span>{t('dataTable.range', { from: safePage * pageSize + 1, to: Math.min((safePage + 1) * pageSize, total), total })}</span>
          <div className="flex items-center gap-1">
            <button className="tk-btn-ghost px-2 py-1 disabled:opacity-40" disabled={safePage === 0} onClick={() => goToPage(safePage - 1)}><ChevronLeft size={14} className="rtl:rotate-180" /></button>
            <span className="tk-num px-1">{safePage + 1} / {pageCount}</span>
            <button className="tk-btn-ghost px-2 py-1 disabled:opacity-40" disabled={safePage >= pageCount - 1} onClick={() => goToPage(safePage + 1)}><ChevronRight size={14} className="rtl:rotate-180" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

/** "Columns" dropdown — toggle visibility of hideable columns. */
function ColumnsMenu<T>({ columns, hiddenSet, onToggle, label }: {
  columns: Column<T>[];
  hiddenSet: Set<string>;
  onToggle: (key: string) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button className="tk-btn-ghost px-2 py-1.5 text-caption" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} title={label}>
        <Columns3 size={14} /> {label}
      </button>
      {open && (
        <div role="menu" className="tk-modal absolute end-0 z-dropdown mt-1 w-52 overflow-hidden p-1">
          {columns.map((c) => {
            const visible = !hiddenSet.has(c.key);
            return (
              <button
                key={c.key}
                role="menuitemcheckbox"
                aria-checked={visible}
                onClick={() => onToggle(c.key)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-white/5"
              >
                <span className={`grid h-4 w-4 place-items-center rounded border ${visible ? 'border-primary bg-primary text-white' : 'border-white/20'}`}>
                  {visible && <Check size={11} />}
                </span>
                <span className="truncate">{c.header || c.key}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <h1 className="text-title font-semibold">{title}</h1>
        {subtitle && <p className="mt-0.5 text-label text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
